import { useState } from "react";
import { teamOverview } from "../helpers/calibrationOverview";
import { detailFields, leagueFields } from "../helpers/leagueAnalysisDraft";
export default function CalibrationReview({
  context,
  preview,
  normalized,
  targets,
  automaticTargets,
  busy,
  pending,
  onDetail,
  en = false,
}) {
  const [view, setView] = useState("summary");
  const [sort, setSort] = useState("draft_strength");
  const txt = (de, english) => (en ? english : de);
  const fmt = (v) =>
    typeof v === "number" && Number.isFinite(v)
      ? v.toLocaleString(en ? "en-GB" : "de-DE", {
          maximumFractionDigits: 3,
          minimumFractionDigits: 3,
        })
      : "—";
  const labels = {
    total_strength: txt("Stärke", "Strength"),
    total_goals: "Goal Sum",
    base: txt("Basiswerte", "Base values"),
    for_home: "For Home",
    against_home: "Against Home",
    for_away: "For Away",
    against_away: "Against Away",
    stored_goal_average: txt("Torschnitt", "Goal average"),
    exp_home_factor: txt("Heimvorteil", "Home advantage"),
    drawfactor: txt("Remisfaktor", "Draw factor"),
  };
  const delta = (a, b) =>
    Math.abs(b - a) < 0.0005
      ? "="
      : `${b > a ? "↑" : "↓"} ${fmt(Math.abs(b - a))}`;
  const teams = context.teams
    .map((team) => {
      const draft = preview.rows.find((r) => r.team_id === team.team_id);
      return { team, draft, overview: teamOverview(team, draft) };
    })
    .sort((a, b) => {
      const metric = sort.endsWith("goals") ? "total_goals" : "total_strength";
      const source = sort.startsWith("saved") ? "saved" : "draft";
      return (
        (b.overview.sources[source][metric] ?? -Infinity) -
          (a.overview.sources[source][metric] ?? -Infinity) ||
        a.team.name.localeCompare(b.team.name)
      );
    });
  const changed = teams.filter(({ team, draft }) =>
    detailFields.some((f) => Math.abs(team.setting[f] - draft[f]) > 1e-10)
  );
  const manual = teams.some(({ team }) =>
    detailFields.some(
      (f) =>
        targets?.[team.team_id]?.[f] !== automaticTargets?.[team.team_id]?.[f]
    )
  );
  return (
    <section
      className="calibration-review"
      aria-label={txt("Liga-Entwurfsprüfung", "League draft review")}
    >
      <p role="status" className="la-draft-state">
        {manual
          ? txt("Manuell geänderter Entwurf", "Manually edited draft")
          : txt("Automatik-Entwurf", "Automatic draft")}{" "}
        · {txt("vor Normalisierung", "before normalization")}
      </p>
      <div
        className="la-review-tabs"
        role="group"
        aria-label={txt("Prüfansicht", "Review view")}
      >
        <button
          aria-pressed={view === "summary"}
          onClick={() => setView("summary")}
        >
          Strength + Goal Sum
        </button>
        <button aria-pressed={view === "base"} onClick={() => setView("base")}>
          {labels.base}
        </button>
        <label>
          {txt("Sortierung", "Sort")}
          <select
            aria-label={txt("Sortierung", "Sort")}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            {[
              "draft_strength",
              "saved_strength",
              "draft_goals",
              "saved_goals",
            ].map((k) => (
              <option key={k} value={k}>
                {k.endsWith("strength") ? "Strength" : "Goal Sum"} ·{" "}
                {k.startsWith("draft")
                  ? txt("Vorschlag", "Proposal")
                  : txt("Gespeichert", "Saved")}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="le-scroll la-cockpit-scroll">
        <table
          className="la-cockpit-table"
          aria-label={txt("Kalibrierungsübersicht", "Calibration overview")}
        >
          <thead>
            <tr>
              <th className="la-rank">#</th>
              <th className="la-identity-col">Team</th>
              {(view === "base"
                ? detailFields
                : ["total_strength", "total_goals"]
              ).map((f) => (
                <th key={f}>
                  {labels[f]}
                  <small>
                    {txt("Gespeichert → Vorschlag", "Saved → Proposal")}
                  </small>
                </th>
              ))}
              <th>{txt("Δ Strength / Goal Sum", "Δ Strength / Goal Sum")}</th>
              <th>{txt("Warum?", "Why?")}</th>
            </tr>
          </thead>
          <tbody>
            {teams.map(({ team, draft, overview }, index) => (
              <tr key={team.team_id} data-team-id={team.team_id}>
                <th className="la-rank" scope="row">
                  {index + 1}
                </th>
                <th className="la-identity-col">
                  <span className="la-team-name">
                    {team.logo && <img src={team.logo} alt="" />}
                    {team.name}
                  </span>
                </th>
                {(view === "base"
                  ? detailFields
                  : ["total_strength", "total_goals"]
                ).map((f) => (
                  <td key={f} data-field={f}>
                    <span>
                      {fmt(
                        view === "base"
                          ? team.setting[f]
                          : overview.sources.saved[f]
                      )}
                    </span>
                    <span className="la-value-arrow"> → </span>
                    <strong className="la-primary-value">
                      {fmt(
                        view === "base" ? draft[f] : overview.sources.draft[f]
                      )}
                    </strong>
                  </td>
                ))}
                <td>
                  {delta(
                    overview.sources.saved.total_strength,
                    overview.sources.draft.total_strength
                  )}{" "}
                  /{" "}
                  {delta(
                    overview.sources.saved.total_goals,
                    overview.sources.draft.total_goals
                  )}
                </td>
                <td>
                  <button
                    disabled={busy}
                    aria-label={`${team.name} Details`}
                    onClick={() => onDetail(team.team_id)}
                  >
                    {txt("Warum? / Korrigieren", "Why? / Edit")}
                  </button>
                  {overview.diagnostics.length > 0 && (
                    <span
                      title={txt(
                        "Gesamtbild im Detail prüfen; nur Diagnose",
                        "Review overall picture in detail; diagnostic only"
                      )}
                    >
                      {" "}
                      !
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        {changed.length} {txt("veränderte Teams", "changed teams")} ·{" "}
        {preview.summary.changed_details}{" "}
        {txt(
          "veränderte Kernsettings im Entwurf",
          "changed draft base settings"
        )}
      </p>
      <details className="la-save-review">
        <summary>
          {txt(
            "Save-Vorschau · Nach Normalisierung",
            "Save preview · After normalization"
          )}
        </summary>
        {pending ? (
          <p role="status">
            {txt(
              "Entwurf lokal aktualisiert. Bitte Save-Vorschau prüfen, bevor gespeichert wird.",
              "Draft updated locally. Check the save preview before saving."
            )}
          </p>
        ) : (
          normalized && (
            <>
              <p>
                {txt(
                  "Gespeichert = Ausgangsstand. Entwurf = aktuelle Arbeitswerte. Nach Normalisierung = Vorschau des gemeinsamen Saves einschließlich Saisonparametern.",
                  "Saved = baseline. Draft = current working values. After normalization = preview of the paired save including season parameters."
                )}
              </p>
              <p>
                {txt(
                  "Markierte finale Werte ändern sich zusätzlich durch Normalisierung. Sie werden beim Save verwendet.",
                  "Highlighted final values change further during normalization. These are used on save."
                )}
              </p>
              <div className="le-scroll">
                <table
                  aria-label={txt(
                    "Normalisierungsvorschau",
                    "Normalization preview"
                  )}
                >
                  <thead>
                    <tr>
                      <th>Team</th>
                      <th>{txt("Größe", "Metric")}</th>
                      <th>{txt("Gespeichert", "Saved")}</th>
                      <th>{txt("Entwurf", "Draft")}</th>
                      <th>
                        {txt("Nach Normalisierung", "After normalization")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams.flatMap(({ team, draft, overview }) =>
                      ["total_strength", "total_goals", ...detailFields].map(
                        (f, i) => (
                          <tr key={team.team_id + f}>
                            {i === 0 && <th rowSpan={6}>{team.name}</th>}
                            <th>{labels[f]}</th>
                            <td>
                              {fmt(
                                overview.sources.saved[f] ?? team.setting[f]
                              )}
                            </td>
                            <td>{fmt(draft[f])}</td>
                            <td
                              className={
                                Math.abs(
                                  (normalized.rows.find(
                                    (r) => r.team_id === team.team_id
                                  )?.[f] ?? draft[f]) - draft[f]
                                ) > 1e-10
                                  ? "la-normalized-change"
                                  : undefined
                              }
                            >
                              {fmt(
                                normalized.rows.find(
                                  (r) => r.team_id === team.team_id
                                )?.[f]
                              )}
                            </td>
                          </tr>
                        )
                      )
                    )}
                  </tbody>
                </table>
              </div>
              <p>
                <b>{txt("Modellparameter", "Model parameters")}</b>
              </p>
              {leagueFields.map((f) => (
                <p key={f}>
                  {labels[f]}: {fmt(context.parameters[f])} /{" "}
                  {fmt(preview.parameters[f])} / {fmt(normalized.parameters[f])}
                </p>
              ))}
              <p>
                {txt(
                  "Speichern verwendet den bestehenden transaktionalen Save mit Snapshot und Restore-Fähigkeit.",
                  "Saving uses the existing transactional save with snapshot and restore support."
                )}
              </p>
            </>
          )
        )}
      </details>
    </section>
  );
}
